import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, FormControl, FormLabel, TextField, RadioGroup, FormControlLabel, Radio, Typography, InputAdornment } from "@mui/material";
import { showErrorToaster, showSuccessToaster } from "../../helpers/utils/toaster";
import companyCreditBalanceService from "../../services/companyCreditBalance.service";

const CompanyCreditAdjustment = ({ onClose, companyInfo }) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      adjustmentType: "credit",
      amount: "",
      message: "",
    },
  });

  const onSubmit = (data) => {
    setLoading(true);

    companyCreditBalanceService
      .adjustCompanyCreditBalance({
        companyId: companyInfo.id,
        adjustmentType: data.adjustmentType,
        amount: data.amount,
        message: data.message,
      })
      .then((res) => {
        if (res.status) {
          showSuccessToaster(res.message);
          onClose();
        } else {
          showErrorToaster(res.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Adjust the credit balance for <strong>{companyInfo?.name}</strong>. You can choose to add (Credit) or deduct (Debit) from their current balance.
      </Typography>

      <FormControl component="fieldset" required>
        <FormLabel component="legend" sx={{ mb: 1, fontWeight: "medium" }}>
          Adjustment Type
        </FormLabel>
        <Controller
          name="adjustmentType"
          control={control}
          render={({ field }) => (
            <RadioGroup row {...field} sx={{ gap: 4 }}>
              <FormControlLabel
                value="credit"
                control={<Radio color="success" />}
                label={
                  <Typography color="success.main" fontWeight="medium">
                    Credit (+ Add)
                  </Typography>
                }
              />
              <FormControlLabel
                value="debit"
                control={<Radio color="error" />}
                label={
                  <Typography color="error.main" fontWeight="medium">
                    Debit (- Deduct)
                  </Typography>
                }
              />
            </RadioGroup>
          )}
        />
      </FormControl>

      <FormControl required>
        <FormLabel sx={{ mb: 1, fontWeight: "medium" }}>Amount</FormLabel>
        <TextField
          size="small"
          type="number"
          placeholder="Enter adjustment amount"
          {...register("amount", {
            required: "Amount is required",
            valueAsNumber: true,
            min: { value: 0.01, message: "Amount must be greater than 0" },
          })}
          error={!!errors.amount}
          helperText={errors.amount?.message}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </FormControl>

      <FormControl required>
        <FormLabel sx={{ mb: 1, fontWeight: "medium" }}>Message / Reason</FormLabel>
        <TextField
          size="small"
          multiline
          rows={3}
          placeholder="Enter the reason for this adjustment (e.g., Refund, Bonus, Correction)"
          {...register("message", {
            required: "Message is required",
            minLength: { value: 3, message: "Message must be at least 3 characters" },
          })}
          error={!!errors.message}
          helperText={errors.message?.message}
        />
      </FormControl>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={loading} color="primary">
          Submit Adjustment
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyCreditAdjustment;
